import { AnimatePresence, motion } from 'motion/react';
import { staggerItem } from '@/lib/motion';
import { SidebarSection } from '@reflex/ui/components/sidebar/SidebarSection';
import { ArchivedAgentRow } from '../ArchivedAgentRow';
import type { Agent } from '@/lib/types';

interface ArchivedAgentSectionProps {
  agents: Agent[];
  show: boolean;
  /**
   * Whether the rows sit behind a collapsible "Archived (n)" header. False
   * under the Archived filter, where these rows are the whole sidebar: a
   * header naming the one thing on screen is noise, and its toggle could only
   * ever blank the list the filter just asked for.
   */
  collapsible?: boolean;
  selectedId: string | null;
  onToggleShow: () => void;
  onSelect: (agent: Agent) => void;
  onUnarchive: (agent: Agent) => void;
  onDelete: (agent: Agent) => void;
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (agent: Agent, shiftKey: boolean) => void;
}

export function ArchivedAgentSection({
  agents,
  show,
  collapsible = true,
  selectedId,
  onToggleShow,
  onSelect,
  onUnarchive,
  onDelete,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
}: ArchivedAgentSectionProps) {
  if (agents.length === 0) return null;

  const rows = (
    <AnimatePresence initial={false}>
      {agents.map((agent) => (
        <motion.div key={agent.id} {...staggerItem}>
          <ArchivedAgentRow
            agent={agent}
            selected={selectedId === agent.id}
            onSelect={onSelect}
            onUnarchive={onUnarchive}
            onDelete={onDelete}
            selectionMode={selectionMode}
            checked={selectedIds?.has(agent.id) ?? false}
            onToggleSelect={onToggleSelect}
          />
        </motion.div>
      ))}
    </AnimatePresence>
  );

  // Bare rows keep the testid, so a caller can find the archived runs the same
  // way whether or not they are behind the header.
  if (!collapsible) {
    return <div data-testid="agent-sidebar-section-archived">{rows}</div>;
  }

  return (
    <div className="pt-1">
      <div className="mx-3 h-px bg-border/30" />
      <SidebarSection
        label={`Archived (${agents.length})`}
        open={show}
        onOpenChange={() => onToggleShow()}
        data-testid="agent-sidebar-section-archived"
        triggerTestId="agent-sidebar-section-toggle-archived"
      >
        {rows}
      </SidebarSection>
    </div>
  );
}
